import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import SharedHeader from "@/components/SharedHeader";
import SharedFooter from "@/components/SharedFooter";
import SEO, { serviceSchema, breadcrumbSchema, faqSchema } from "@/components/SEO";
import {
  GraduationCap,
  Users,
  BarChart3,
  Shield,
  CheckCircle,
  ArrowRight,
  Clock,
  IndianRupee,
  BookOpen,
  TrendingUp,
  Bell,
  FileText,
  Megaphone,
  School,
  Sparkles,
  Target,
  ChevronRight,
  Building2,
  Languages,
  Wallet,
  MessageSquare,
  Download
} from "lucide-react";

const SCHOOL_PRICING = {
  starter: {
    name: "Starter",
    students: "25-100",
    price: 999,
    dailyLimit: "1 hour",
    teachersIncluded: 5,
    whatsappQuota: 500,
    whatsappOverage: 0.50,
    description: "For small schools getting started",
    features: [
      "All subjects included",
      "5 teacher accounts",
      "Basic analytics",
      "Parent access portal",
      "Grade-wise reports",
      "500 WhatsApp broadcasts/mo",
    ],
    notIncluded: ["Advanced analytics", "Priority support"]
  },
  plus: {
    name: "Plus",
    students: "101-300",
    price: 1499,
    dailyLimit: "2 hours",
    teachersIncluded: 15,
    whatsappQuota: 1500,
    whatsappOverage: 0.40,
    popular: true,
    description: "Most popular for mid-size schools",
    features: [
      "Everything in Starter",
      "15 teacher accounts",
      "Advanced analytics",
      "Practice tests & leaderboard",
      "In-app announcements",
      "1,500 WhatsApp broadcasts/mo",
    ],
    notIncluded: ["Priority support"]
  },
  pro: {
    name: "Pro",
    students: "301-500",
    price: 1999,
    dailyLimit: "4 hours",
    teachersIncluded: 25,
    whatsappQuota: 2500,
    whatsappOverage: 0.30,
    description: "Complete solution for large schools",
    features: [
      "Everything in Plus",
      "25 teacher accounts",
      "AI-powered insights",
      "WhatsApp & email alerts",
      "Priority support",
      "2,500 WhatsApp broadcasts/mo",
    ],
    notIncluded: []
  }
};

const BENEFITS = [
  {
    icon: Target,
    title: "Adaptive Learning",
    description: "AI adjusts difficulty based on each student's performance, ensuring optimal challenge levels.",
    bgColor: "bg-gradient-to-br from-blue-50 to-cyan-50",
    borderColor: "border-blue-200",
    iconBg: "bg-blue-100",
    iconColor: "text-blue-600",
  },
  {
    icon: Languages,
    title: "11 Indian Languages",
    description: "Content delivered in Hindi, Tamil, Telugu, Kannada, Marathi, Punjabi, and English.",
    bgColor: "bg-gradient-to-br from-purple-50 to-pink-50",
    borderColor: "border-purple-200",
    iconBg: "bg-purple-100",
    iconColor: "text-purple-600",
  },
  {
    icon: BarChart3,
    title: "Real-time Analytics",
    description: "Track class performance, identify struggling students, and monitor engagement metrics.",
    bgColor: "bg-gradient-to-br from-emerald-50 to-teal-50",
    borderColor: "border-emerald-200",
    iconBg: "bg-emerald-100",
    iconColor: "text-emerald-600",
  },
  {
    icon: Users,
    title: "Bulk Student Management",
    description: "CSV upload, bulk actions, and streamlined onboarding for entire classes.",
    bgColor: "bg-gradient-to-br from-amber-50 to-orange-50",
    borderColor: "border-amber-200",
    iconBg: "bg-amber-100",
    iconColor: "text-amber-600",
  },
  {
    icon: Megaphone,
    title: "School Announcements",
    description: "Send notices to parents directly through the platform with read receipts.",
    bgColor: "bg-gradient-to-br from-rose-50 to-pink-50",
    borderColor: "border-rose-200",
    iconBg: "bg-rose-100",
    iconColor: "text-rose-600",
  },
  {
    icon: FileText,
    title: "Exportable Reports",
    description: "Download student performance, billing, and analytics data in CSV format.",
    bgColor: "bg-gradient-to-br from-indigo-50 to-violet-50",
    borderColor: "border-indigo-200",
    iconBg: "bg-indigo-100",
    iconColor: "text-indigo-600",
  },
  {
    icon: Shield,
    title: "DPDP Compliant",
    description: "Full compliance with India's Digital Personal Data Protection Act 2023.",
    bgColor: "bg-gradient-to-br from-sky-50 to-blue-50",
    borderColor: "border-sky-200",
    iconBg: "bg-sky-100",
    iconColor: "text-sky-600",
  },
  {
    icon: Wallet,
    title: "Flexible Billing",
    description: "Net 30 payment terms, PO number support, and transparent per-student pricing.",
    bgColor: "bg-gradient-to-br from-lime-50 to-green-50",
    borderColor: "border-lime-200",
    iconBg: "bg-lime-100",
    iconColor: "text-lime-600",
  }
];

export default function SchoolsLanding() {
  const navigate = useNavigate();

  // SEO structured data for EduSphere
  const eduSphereStructuredData = {
    "@context": "https://schema.org",
    "@graph": [
      serviceSchema({
        name: "EduSphere AI - School Learning Platform",
        description: "AI-powered learning platform for schools and institutions. Personalized learning paths, real-time analytics, teacher dashboard, and DPDP compliance. Volume pricing for 25-1000+ students.",
        price: "999"
      }),
      breadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "EduSphere for Schools", url: "/edusphere" }
      ]),
      faqSchema([
        {
          question: "What is EduSphere AI?",
          answer: "EduSphere AI is a comprehensive school learning platform that provides personalized AI tutoring for students in grades 5-8, with teacher dashboards, real-time analytics, and parent communication tools."
        },
        {
          question: "How much does EduSphere cost for schools?",
          answer: "EduSphere offers volume pricing starting at ₹999/student/month for 25-100 students, with discounts for larger schools. Enterprise plans are available for 1000+ students."
        },
        {
          question: "Does EduSphere support multiple languages?",
          answer: "Yes, EduSphere supports 11 Indian languages: English, Hindi, Tamil, Telugu, Kannada, Marathi, Punjabi, Bengali, Gujarati, Malayalam, and Odia."
        }
      ])
    ]
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <SEO
        title="EduSphere AI - School Learning Platform | B2B EdTech | Eduzy"
        description="Transform your school with EduSphere AI. Personalized learning for grades 5-8, teacher dashboards, real-time analytics, and DPDP compliance. Volume pricing from ₹999/student. Contact us for a demo."
        keywords="school learning platform, B2B EdTech, AI tutoring schools, classroom technology, teacher dashboard, student analytics, CBSE schools, school management system"
        canonicalUrl="/edusphere"
        structuredData={eduSphereStructuredData}
      />
      
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <SharedHeader 
          transparent={true}
          rightContent={
            <div className="flex items-center gap-3">
              <Button
                onClick={() => navigate("/login")}
                variant="outline"
                className="border-emerald-600 text-emerald-600 hover:bg-emerald-50"
                data-testid="school-login-btn"
              >
                School Login
              </Button>
              <Button
                onClick={() => navigate("/school/register")}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="school-register-btn"
              >
                Register School
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          }
        />
      </nav>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-emerald-50 via-white to-blue-50 py-20 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-emerald-200/30 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 -left-20 w-64 h-64 bg-blue-200/30 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-emerald-100 text-emerald-700 mb-6">
                <Building2 className="w-3 h-3 mr-1" />
                For Schools & Institutions
              </Badge>
              <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6 leading-tight">
                Transform Your School with
                <span className="text-emerald-600"> AI-Powered Learning</span>
              </h1>
              <p className="text-lg text-slate-600 mb-8">
                Empower students in Grades 5-8 with personalized AI tutoring. 
                Track performance, engage parents, and improve outcomes — all from one platform.
              </p>
              
              <div className="flex flex-wrap gap-4 mb-8">
                <div className="flex items-center gap-2 text-slate-600">
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                  <span>50+ students per grade pricing</span>
                </div>
                <div className="flex items-center gap-2 text-slate-600">
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                  <span>Net 30 payment terms</span>
                </div>
                <div className="flex items-center gap-2 text-slate-600">
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                  <span>Dedicated support</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-4">
                <Button
                  size="lg"
                  onClick={() => navigate("/school/register")}
                  className="bg-emerald-600 hover:bg-emerald-700 text-lg px-8"
                  data-testid="hero-register-btn"
                >
                  Start Free Pilot
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })}
                  className="text-lg px-8"
                >
                  View Pricing
                </Button>
              </div>
            </div>

            {/* Feature Cards */}
            <div className="grid grid-cols-2 gap-4">
              <Card className="border-0 shadow-lg bg-white">
                <CardContent className="pt-6">
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mb-4">
                    <Target className="w-6 h-6 text-emerald-600" />
                  </div>
                  <h3 className="font-semibold text-slate-800 mb-1">Adaptive AI</h3>
                  <p className="text-sm text-slate-500">Personalized learning paths for each student</p>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-lg bg-white">
                <CardContent className="pt-6">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                    <Languages className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="font-semibold text-slate-800 mb-1">7 Languages</h3>
                  <p className="text-sm text-slate-500">Hindi, Tamil, Telugu, and more</p>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-lg bg-white">
                <CardContent className="pt-6">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4">
                    <BarChart3 className="w-6 h-6 text-purple-600" />
                  </div>
                  <h3 className="font-semibold text-slate-800 mb-1">Real-time Analytics</h3>
                  <p className="text-sm text-slate-500">Track class & student performance</p>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-lg bg-white">
                <CardContent className="pt-6">
                  <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-4">
                    <Shield className="w-6 h-6 text-amber-600" />
                  </div>
                  <h3 className="font-semibold text-slate-800 mb-1">DPDP Compliant</h3>
                  <p className="text-sm text-slate-500">Full data privacy compliance</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Why Eduzy for Schools */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-emerald-100 text-emerald-700 mb-4">Why Schools Choose Eduzy</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Everything Your School Needs
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              A complete learning solution designed specifically for Indian schools,
              with features that teachers and administrators actually need.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {BENEFITS.map((benefit, index) => (
              <Card key={index} className={`border ${benefit.borderColor} ${benefit.bgColor} shadow-sm hover:shadow-lg transition-shadow`}>
                <CardContent className="pt-6">
                  <div className={`w-12 h-12 ${benefit.iconBg} rounded-xl flex items-center justify-center mb-4`}>
                    <benefit.icon className={`w-6 h-6 ${benefit.iconColor}`} />
                  </div>
                  <h3 className="font-semibold text-slate-800 mb-2">{benefit.title}</h3>
                  <p className="text-sm text-slate-500">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-blue-100 text-blue-700 mb-4">Simple Onboarding</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Get Started in 3 Easy Steps
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-500 text-white rounded-2xl flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                1
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-2">Register Your School</h3>
              <p className="text-slate-600">
                Fill out a simple form with your school details. Verification typically takes 24-48 hours.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-500 text-white rounded-2xl flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                2
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-2">Upload Students</h3>
              <p className="text-slate-600">
                Bulk upload students via CSV or add them individually. Parents receive automatic invites.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-500 text-white rounded-2xl flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                3
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-2">Start Learning</h3>
              <p className="text-slate-600">
                Students begin learning immediately. Track progress from your admin dashboard.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-emerald-100 text-emerald-700 mb-4">Transparent B2B Pricing</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              All Subjects Included
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Simple per-student pricing with all subjects included. Volume-based tiers
              with more features as you grow. Contact us for custom enterprise pricing.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {Object.entries(SCHOOL_PRICING).map(([key, plan]) => (
              <Card 
                key={key} 
                className={`relative border-2 flex flex-col ${plan.popular ? 'border-emerald-500 shadow-xl' : 'border-slate-200'}`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <Badge className="bg-emerald-500 text-white px-4 py-1">Most Popular</Badge>
                  </div>
                )}
                <CardHeader className="text-center pb-2">
                  <CardTitle className="text-xl">{plan.name}</CardTitle>
                  <CardDescription>{plan.description}</CardDescription>
                  <div className="flex justify-center mt-3">
                    <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold ${
                      plan.popular 
                        ? 'bg-emerald-100 text-emerald-700 border border-emerald-200' 
                        : 'bg-indigo-100 text-indigo-700 border border-indigo-200'
                    }`}>
                      <Users className="w-4 h-4" />
                      {plan.students} students
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="text-center flex-1 flex flex-col">
                  <div className="mb-4">
                    <div className="flex items-center justify-center gap-1">
                      <IndianRupee className="w-6 h-6 text-slate-400" />
                      <span className="text-4xl font-bold text-slate-800">{plan.price}</span>
                    </div>
                    <p className="text-slate-500 text-sm">per student/month</p>
                    <p className="text-orange-600 text-xs mt-1">+ 18% GST</p>
                  </div>

                  <div className="mb-4 flex flex-wrap justify-center gap-2">
                    <div className="inline-flex items-center gap-1.5 bg-slate-100 rounded-full px-3 py-1.5">
                      <Clock className="w-3.5 h-3.5 text-slate-500" />
                      <span className="text-xs font-medium text-slate-700">{plan.dailyLimit}/day</span>
                    </div>
                    <div className="inline-flex items-center gap-1.5 bg-blue-100 rounded-full px-3 py-1.5">
                      <Users className="w-3.5 h-3.5 text-blue-600" />
                      <span className="text-xs font-medium text-blue-700">{plan.teachersIncluded} teachers</span>
                    </div>
                    <div className="inline-flex items-center gap-1.5 bg-emerald-100 rounded-full px-3 py-1.5">
                      <MessageSquare className="w-3.5 h-3.5 text-emerald-600" />
                      <span className="text-xs font-medium text-emerald-700">{plan.whatsappQuota.toLocaleString()} broadcasts</span>
                    </div>
                  </div>
                  
                  <div className="mb-4 text-center">
                    <p className="text-xs text-gray-500">
                      Overage: ₹{plan.whatsappOverage}/message after quota
                    </p>
                  </div>

                  <div className="space-y-2.5 text-left mb-6 flex-1">
                    {plan.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-slate-600">{feature}</span>
                      </div>
                    ))}
                    {plan.notIncluded.map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-2 opacity-50">
                        <div className="w-4 h-4 rounded-full border-2 border-slate-300 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-slate-400 line-through">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <Button
                    onClick={() => navigate("/school/register")}
                    className={`w-full mt-auto ${plan.popular ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-gray-700 hover:bg-gray-800'}`}
                  >
                    Get Started
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Enterprise CTA */}
          <div className="mt-12 text-center bg-slate-50 rounded-2xl p-8">
            <h3 className="text-xl font-semibold text-slate-800 mb-2">Need 500+ Students?</h3>
            <p className="text-slate-600 mb-4">
              Get custom enterprise pricing with dedicated support and volume discounts.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button variant="outline" onClick={() => navigate("/school/register")}>
                Contact Sales
              </Button>
              <Button 
                variant="outline"
                onClick={() => window.open('/edusphere-plans-brochure.html', '_blank')}
                className="border-emerald-600 text-emerald-600 hover:bg-emerald-50"
              >
                <Download className="w-4 h-4 mr-2" />
                Download Brochure
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-emerald-600 to-teal-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Transform Learning at Your School?
          </h2>
          <p className="text-lg text-emerald-100 mb-8">
            Start with a free 30-day pilot for one class.
            No commitment required.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button
              size="lg"
              onClick={() => navigate("/school/register")}
              className="bg-white hover:bg-gray-100 text-emerald-700 text-lg px-8 shadow-lg"
            >
              Register Your School
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => window.location.href = "mailto:info@contentiumtech.com"}
              className="text-lg px-8 border-white/40 text-white hover:bg-white/10"
            >
              Contact Sales
            </Button>
          </div>
          <p className="text-emerald-200 mt-6 text-sm">
            Questions? Email us at <a href="mailto:info@contentiumtech.com" className="text-white hover:underline">info@contentiumtech.com</a>
          </p>
        </div>
      </section>

      {/* Footer */}
      <SharedFooter />
    </div>
  );
}
